<template>
  <div
    class="article-detail-skeleton"
    aria-busy="true"
    aria-live="polite">
    <section class="article-detail-skeleton__hero hero-banner">
      <div class="article-detail-skeleton__hero-image">
        <PrePeersSkeleton variant="block" />
      </div>
    </section>

    <div class="article-detail-skeleton__breadcrumb container">
      <PrePeersSkeleton
        variant="text"
        width="min(300px, 72%)"
        height="0.875rem" />
    </div>

    <section class="hero-section">
      <div class="container article-detail-skeleton__hero-text">
        <PrePeersSkeleton
          variant="text"
          height="2.25rem"
          width="min(520px, 92%)" />
        <PrePeersSkeleton
          variant="text"
          width="100%" />
        <PrePeersSkeleton
          variant="text"
          width="90%" />
        <PrePeersSkeleton
          variant="text"
          width="72%" />
        <PrePeersSkeleton
          variant="text"
          height="0.875rem"
          width="min(200px, 50%)" />
        <PrePeersSkeleton
          variant="text"
          width="120px"
          height="2px" />
      </div>
    </section>

    <section class="level-nav-section">
      <div class="container article-detail-skeleton__nav">
        <PrePeersSkeleton
          variant="text"
          height="1.25rem"
          width="min(280px, 75%)" />
        <div class="article-detail-skeleton__nav-links">
          <PrePeersSkeleton
            v-for="linkIndex in 4"
            :key="linkIndex"
            variant="text"
            height="2rem"
            width="108px" />
        </div>
      </div>
    </section>

    <section class="content-section">
      <div class="container article-detail-skeleton__content">
        <PrePeersSkeleton
          v-for="lineIndex in 6"
          :key="`content-${lineIndex}`"
          variant="text"
          :width="lineIndex === 6 ? '48%' : '100%'" />
      </div>
    </section>

    <section class="article-detail-skeleton__author">
      <PrePeersSkeleton
        variant="circle"
        width="56px"
        height="56px" />
      <PrePeersSkeleton
        variant="text"
        width="140px"
        height="1rem" />
    </section>

    <section
      v-if="props.showMediaGrids"
      class="media-grid-section">
      <div class="container article-detail-skeleton__media-grid">
        <PrePeersSkeleton
          variant="text"
          height="1.5rem"
          width="min(360px, 85%)" />
        <PrePeersSkeleton
          variant="text"
          width="min(480px, 90%)" />
        <div class="article-detail-skeleton__cards">
          <PrePeersSkeleton
            v-for="cardIndex in 3"
            :key="cardIndex"
            variant="block"
            class="article-detail-skeleton__card" />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import PrePeersSkeleton from '~/components/ui/PrePeersSkeleton.vue'
import type { ArticleDetailSkeletonProps } from '~/types/ArticleDetailSkeleton'

/**
 * Définit les props du composant ArticleDetailSkeleton.
 */
const props: ArticleDetailSkeletonProps = defineProps({
  showMediaGrids: {
    type: Boolean,
    default: true,
  },
})
</script>

<style scoped>
.article-detail-skeleton__hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 280px;
  margin-bottom: 70px;
  overflow: visible;
  background-color: var(--bg-surface);
}

.article-detail-skeleton__hero-image {
  position: absolute;
  top: 50%;
  left: 50%;
  width: clamp(300px, 70vw, 800px);
  height: 220px;
  max-height: 260px;
  transform: translate(-50%, -50%);
  border-radius: var(--radius-lg);
  overflow: hidden;
  z-index: 1;
}

.article-detail-skeleton__hero-image :deep(.prepeers-skeleton) {
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.article-detail-skeleton__breadcrumb {
  margin-bottom: var(--space-4);
}

.article-detail-skeleton__hero-text {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.article-detail-skeleton__nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.article-detail-skeleton__nav-links {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.article-detail-skeleton__content {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.article-detail-skeleton__author {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  margin: var(--space-8) auto;
}

.article-detail-skeleton__media-grid {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.article-detail-skeleton__cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-6);
}

.article-detail-skeleton__card {
  min-height: 220px;
  border-radius: var(--radius-lg);
}

@media (max-width: 1024px) {
  .article-detail-skeleton__cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .article-detail-skeleton__cards {
    grid-template-columns: 1fr;
  }
}
</style>
